import { DeviceDef } from "./DeviceDef";
import { ConversionUtil } from "./ConversionUtil";
import { CameraAccessorDef, CameraOptions } from "./CameraAccessor";
import { OrientationAccessorDef } from "./OrientationAccessorDef";
import { LogManager } from "./logging/LogManager";

const log = LogManager.getLogger("core.DeviceAccessor");

const appTestingKey = 'app-ui-testing';

const defaultDeviceDef: DeviceDef = {
    platform: getPlatform(), browser: getBrowser(), type: getDeviceType(), isVirtual: false
};

const defaultCameraAccessor: CameraAccessorDef = {
    hasCamera: false, takePhoto: (options: CameraOptions) => undefined
};

const defaultOrientationAccessor: OrientationAccessorDef = {
    orientation: () => Promise.resolve({ type: window.screen.orientation.type }),
    lock: (options) => defaultOrientationLockUnlock(),
    unlock: () => defaultOrientationLockUnlock(),
    isLandscape: window.screen.orientation.type.includes('landscape'),
    isPortrait: window.screen.orientation.type.includes('portrait')
}

export interface DeviceAccessorDef {
    device: DeviceDef;
    cameraAccessor: CameraAccessorDef;
    orientationAccessor: OrientationAccessorDef;
    appTesting?: boolean;
}

export class DeviceAccessor {
    private static accessorDef: DeviceAccessorDef = {
        device: defaultDeviceDef,
        cameraAccessor: defaultCameraAccessor,
        orientationAccessor: defaultOrientationAccessor
    };

    static get platform(): string {
        return this.accessorDef.device.platform || getPlatform();
    }

    static get browser(): string {
        return this.accessorDef.device.browser || getBrowser();
    }

    static get isWeb(): boolean {
        return !this.hasCapacitor();
    }

    static get isApp(): boolean {
        return this.hasCapacitor() || this.isAppTestingViaWeb();
    }

    static hasCapacitor(): boolean {
        const cap = window["Capacitor"] ?? window.parent?.["Capacitor"];
        return cap !== undefined;
    }

    static isAppTestingViaWeb(): boolean {
        if (this.accessorDef.appTesting == null) {
            if (this.appTestingAvailable()) {
                const urlParams = new URLSearchParams(window.location.search);
                const paramValue = urlParams.get('app');
                if (paramValue === 'true') {
                    sessionStorage.setItem(appTestingKey, 'true');
                }
                // param may be disappeared during SPA navigation. Rely on storage to know if we're testing the app.
                this.accessorDef.appTesting = ConversionUtil.parseBoolean(sessionStorage.getItem(appTestingKey));
            }
            this.accessorDef.appTesting ??= false
        }
        return this.accessorDef.appTesting;
    }

    static get isTablet() {
        const type = this.accessorDef.device.type ?? getDeviceType();
        return type === 'tablet' || (type === 'desktop' && this.isAppTestingViaWeb());
    }

    private static appTestingAvailable(): boolean {
        return (this.isLocalHost || this.isMcLeodWebServer)
    }

    static get isLocalHost(): boolean {
        return window.location.origin.toLowerCase().indexOf('localhost') > -1
    }

    static get isMcLeodWebServer(): boolean {
        return window.location.origin.toLowerCase().indexOf('tmscorp.com') > -1;
    }

    static isVirtual(): boolean {
        return this.accessorDef.device.isVirtual;
    }

    static get device(): DeviceDef {
        return this.accessorDef.device;
    }

    static set device(def: DeviceDef) {
        if (def) { this.accessorDef.device = def; }
    }

    static get cameraAccessor(): CameraAccessorDef {
        return this.accessorDef.cameraAccessor;
    }

    static set cameraAccessor(def: CameraAccessorDef) {
        if (def) { this.accessorDef.cameraAccessor = def; }
    }

    static get orientationAccessor(): OrientationAccessorDef {
        return this.accessorDef.orientationAccessor;
    }

    static set orientationAccessor(def: OrientationAccessorDef) {
        if (def) { this.accessorDef.orientationAccessor = def; }
    }
}

function getPlatform(): string {
    const platform = navigator.platform?.toLowerCase();
    return platform.indexOf("win") != -1 ? "Windows"
        : platform.indexOf("Mac") != -1 ? "MacOS"
            : platform.indexOf("X11") != -1 ? "UNIX"
                : platform.indexOf("Linux") != -1 ? "Linux"
                    : platform;
}

function getBrowser(): string {
    const userAgent = window.navigator.userAgent.toLowerCase();
    return userAgent.indexOf('edg') > -1 ? 'Edge'
        : userAgent.indexOf('opr') > -1 && !!window["opr"] ? 'Opera'
            : userAgent.indexOf('chrome') > -1 && !!window["chrome"] ? 'Chrome'
                : userAgent.indexOf('trident') > -1 ? 'IE'
                    : userAgent.indexOf('firefox') > -1 ? 'Firefox'
                        : userAgent.indexOf('safari') > -1 ? 'Safari'
                            : window.navigator.userAgent;
}

function getDeviceType(): 'phone' | 'tablet' | 'desktop' {
    const ua = navigator.userAgent.toLowerCase();

    const isTabletUA = /ipad|android(?!.*mobile)/.test(ua);
    // Android tablets often don't include "mobile" in UA
    const isAndroidTablet = /android/.test(ua) && !/mobile/.test(ua);
    // Modern iPads on iOS 13+ report as "Macintosh" in UA
    const isiPadOS = (/macintosh/.test(ua) && navigator.maxTouchPoints > 1)

    // Modern iPads on iOS 13+ report as "Macintosh" in UA
    if (isTabletUA || isAndroidTablet || isiPadOS) {
        return 'tablet';
    }

    const isMobileUA = /iphone|ipod|android.*mobile/.test(ua);

    // Screen-based heuristics (using the shorter dimension)
    const screenWidth = Math.min(window.screen.width, window.screen.height);

    if (isMobileUA) {
        // Large phones (like Max/Plus models) can hit ~430+ CSS px wide,
        // but tablets typically start around 600+
        return screenWidth >= 600 ? 'tablet' : 'phone';
    }

    // No mobile/tablet UA signals — but check screen size as a fallback
    // (some tablets in desktop mode, or weird UAs)
    if (screenWidth < 600) {
        return 'phone';
    }

    return 'desktop';
}

function defaultOrientationLockUnlock(): Promise<void> {
    log.debug("Orientation lock/unlock not implemented for standard web application.")
    return Promise.resolve();
}
